import { createSlice } from "@reduxjs/toolkit";
import {
    createPettyCashFundRequest,
    getPettyCashFundRequests,
    approvePettyCashFundRequest,
    rejectPettyCashFundRequest,
    getPettyCashActiveFund,
    closePettyCashFund,
    getPettyCashFundHistory,
    createPettyCashExpense,
    getPettyCashExpenses,
    getPettyCashVoucherTypes,
    createPettyCashVoucherType,
    deletePettyCashVoucherType,
    getPettyCashVendors,
    createPettyCashVendor,
    deletePettyCashVendor,
    getPettyCashBranches,
    createPettyCashBranch,
    deletePettyCashBranch,
    getPettyCashExpenseTypes,
    createPettyCashExpenseType,
    deletePettyCashExpenseType,
    getPettyCashBudgetCodes,
    createPettyCashBudgetCode,
    deletePettyCashBudgetCode,
    getPettyCashReports,
    getPettyCashEmployees
} from "./thunk";

interface PettyCashState {
    // Fund Requests
    fundRequests: any[];
    fundRequestsLoading: boolean;
    fundRequestsError: string | null;
    
    // Active Fund
    activeFund: any | null;
    activeFundLoading: boolean;
    activeFundError: string | null;
    
    // Fund History
    fundHistory: any[];
    fundHistoryLoading: boolean;
    fundHistoryError: string | null;
    
    // Expenses
    expenses: any[];
    expensesLoading: boolean;
    expensesError: string | null;
    
    // Catalogs
    voucherTypes: any[];
    vendors: any[];
    branches: any[];
    expenseTypes: any[];
    budgetCodes: any[];
    employees: any[];
    
    // Reports
    reports: any | null;
    reportsLoading: boolean;
    reportsError: string | null;
    
    // General
    loading: boolean;
    error: string | null;
}

const initialState: PettyCashState = {
    // Fund Requests
    fundRequests: [],
    fundRequestsLoading: false,
    fundRequestsError: null,
    
    // Active Fund
    activeFund: null,
    activeFundLoading: false,
    activeFundError: null,
    
    // Fund History
    fundHistory: [],
    fundHistoryLoading: false,
    fundHistoryError: null,
    
    // Expenses
    expenses: [],
    expensesLoading: false,
    expensesError: null,
    
    // Catalogs
    voucherTypes: [],
    vendors: [],
    branches: [],
    expenseTypes: [],
    budgetCodes: [],
    employees: [],
    
    // Reports
    reports: null,
    reportsLoading: false,
    reportsError: null,
    
    // General
    loading: false,
    error: null,
};

const pettyCashSlice = createSlice({
    name: "pettyCash",
    initialState,
    reducers: {
        clearPettyCashData: (state) => {
            state.fundRequests = [];
            state.activeFund = null;
            state.fundHistory = [];
            state.expenses = [];
            state.voucherTypes = [];
            state.vendors = [];
            state.branches = [];
            state.expenseTypes = [];
            state.budgetCodes = [];
            state.employees = [];
            state.reports = null;
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // ==================== FUND REQUESTS ====================
        
        // Create Fund Request
        builder.addCase(createPettyCashFundRequest.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createPettyCashFundRequest.fulfilled, (state, action) => {
            state.loading = false;
            state.fundRequests.unshift(action.payload);
        });
        builder.addCase(createPettyCashFundRequest.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to create fund request";
        });
        
        // Get Fund Requests
        builder.addCase(getPettyCashFundRequests.pending, (state) => {
            state.fundRequestsLoading = true;
            state.fundRequestsError = null;
        });
        builder.addCase(getPettyCashFundRequests.fulfilled, (state, action) => {
            state.fundRequestsLoading = false;
            state.fundRequests = action.payload;
        });
        builder.addCase(getPettyCashFundRequests.rejected, (state, action) => {
            state.fundRequestsLoading = false;
            state.fundRequestsError = action.error.message || "Failed to get fund requests";
        });
        
        // Approve Fund Request
        builder.addCase(approvePettyCashFundRequest.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(approvePettyCashFundRequest.fulfilled, (state, action) => {
            state.loading = false;
            // Update the request status in the list
            const index = state.fundRequests.findIndex(req => req.id === action.payload.id);
            if (index !== -1) {
                state.fundRequests[index].status = 'approved';
            }
        });
        builder.addCase(approvePettyCashFundRequest.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to approve fund request";
        });
        
        // Reject Fund Request
        builder.addCase(rejectPettyCashFundRequest.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(rejectPettyCashFundRequest.fulfilled, (state, action) => {
            state.loading = false;
            // Update the request status in the list
            const index = state.fundRequests.findIndex(req => req.id === action.payload.id);
            if (index !== -1) {
                state.fundRequests[index].status = 'rejected';
            }
        });
        builder.addCase(rejectPettyCashFundRequest.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to reject fund request";
        });
        
        // ==================== FUND MANAGEMENT ====================
        
        // Get Active Fund
        builder.addCase(getPettyCashActiveFund.pending, (state) => {
            state.activeFundLoading = true;
            state.activeFundError = null;
        });
        builder.addCase(getPettyCashActiveFund.fulfilled, (state, action) => {
            // getPettyCashActiveFund.fulfilled
            state.activeFundLoading = false;
            state.activeFund = action.payload;
        });
        builder.addCase(getPettyCashActiveFund.rejected, (state, action) => {
            state.activeFundLoading = false;
            state.activeFundError = action.error.message || "Failed to get active fund";
        });
        
        // Close Fund
        builder.addCase(closePettyCashFund.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(closePettyCashFund.fulfilled, (state, action) => {
            state.loading = false;
            if (state.activeFund) {
                state.activeFund.status = 'closed';
            }
        });
        builder.addCase(closePettyCashFund.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to close fund";
        });
        
        // Get Fund History
        builder.addCase(getPettyCashFundHistory.pending, (state) => {
            state.fundHistoryLoading = true;
            state.fundHistoryError = null;
        });
        builder.addCase(getPettyCashFundHistory.fulfilled, (state, action) => {
            state.fundHistoryLoading = false;
            state.fundHistory = action.payload;
        });
        builder.addCase(getPettyCashFundHistory.rejected, (state, action) => {
            state.fundHistoryLoading = false;
            state.fundHistoryError = action.error.message || "Failed to get fund history";
        });
        
        // ==================== EXPENSE MANAGEMENT ====================
        
        // Create Expense
        builder.addCase(createPettyCashExpense.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createPettyCashExpense.fulfilled, (state, action) => {
            state.loading = false;
            state.expenses.unshift(action.payload);
            // Update active fund balance
            if (state.activeFund) {
                state.activeFund.current_balance = action.payload.fund_balance;
            }
        });
        builder.addCase(createPettyCashExpense.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to create expense";
        });
        
        // Get Expenses
        builder.addCase(getPettyCashExpenses.pending, (state) => {
            state.expensesLoading = true;
            state.expensesError = null;
        });
        builder.addCase(getPettyCashExpenses.fulfilled, (state, action) => {
            state.expensesLoading = false;
            state.expenses = action.payload;
        });
        builder.addCase(getPettyCashExpenses.rejected, (state, action) => {
            state.expensesLoading = false;
            state.expensesError = action.error.message || "Failed to get expenses";
        });
        
        // ==================== CATALOG MANAGEMENT ====================
        
        // Voucher Types
        builder.addCase(getPettyCashVoucherTypes.fulfilled, (state, action) => {
            state.voucherTypes = action.payload;
        });
        builder.addCase(createPettyCashVoucherType.fulfilled, (state, action) => {
            state.voucherTypes.push(action.payload);
        });
        builder.addCase(deletePettyCashVoucherType.fulfilled, (state, action) => {
            state.voucherTypes = state.voucherTypes.filter(item => item.id !== action.payload);
        });
        
        // Vendors
        builder.addCase(getPettyCashVendors.fulfilled, (state, action) => {
            state.vendors = action.payload;
        });
        builder.addCase(createPettyCashVendor.fulfilled, (state, action) => {
            state.vendors.push(action.payload);
        });
        builder.addCase(deletePettyCashVendor.fulfilled, (state, action) => {
            state.vendors = state.vendors.filter(item => item.id !== action.payload);
        });
        
        // Branches
        builder.addCase(getPettyCashBranches.fulfilled, (state, action) => {
            state.branches = action.payload;
        });
        builder.addCase(createPettyCashBranch.fulfilled, (state, action) => {
            state.branches.push(action.payload);
        });
        builder.addCase(deletePettyCashBranch.fulfilled, (state, action) => {
            state.branches = state.branches.filter(item => item.id !== action.payload);
        });
        
        // Expense Types
        builder.addCase(getPettyCashExpenseTypes.fulfilled, (state, action) => {
            state.expenseTypes = action.payload;
        });
        builder.addCase(createPettyCashExpenseType.fulfilled, (state, action) => {
            state.expenseTypes.push(action.payload);
        });
        builder.addCase(deletePettyCashExpenseType.fulfilled, (state, action) => {
            state.expenseTypes = state.expenseTypes.filter(item => item.id !== action.payload);
        });
        
        // Budget Codes
        builder.addCase(getPettyCashBudgetCodes.fulfilled, (state, action) => {
            state.budgetCodes = action.payload;
        });
        builder.addCase(createPettyCashBudgetCode.fulfilled, (state, action) => {
            state.budgetCodes.push(action.payload);
        });
        builder.addCase(deletePettyCashBudgetCode.fulfilled, (state, action) => {
            state.budgetCodes = state.budgetCodes.filter(item => item.id !== action.payload);
        });
        
        // ==================== REPORTING ====================
        
        // Get Reports
        builder.addCase(getPettyCashReports.pending, (state) => {
            state.reportsLoading = true;
            state.reportsError = null;
        });
        builder.addCase(getPettyCashReports.fulfilled, (state, action) => {
            state.reportsLoading = false;
            state.reports = action.payload;
        });
        builder.addCase(getPettyCashReports.rejected, (state, action) => {
            state.reportsLoading = false;
            state.reportsError = action.error.message || "Failed to get reports";
        });
        
        // ==================== UTILITIES ====================
        
        // Get Employees
        builder.addCase(getPettyCashEmployees.fulfilled, (state, action) => {
            // Redux: Setting employees data
            state.employees = action.payload;
        });
        builder.addCase(getPettyCashEmployees.rejected, (state, action) => {
            // Redux: Failed to get employees
            state.employees = [];
        });
    },
});

export const { clearPettyCashData, clearError } = pettyCashSlice.actions;
export default pettyCashSlice.reducer;
